import { Component } from '@theme/component';
import { SlideshowSelectEvent } from '@theme/events';
import { prefersReducedMotion } from '@theme/utilities';

const GRID_QUERY = '(min-width: 750px)';
const VERTICAL_INDEX_QUERY = '(min-width: 990px)';
const HISTORY_NAMESPACE = 'ulifShopBySolution';
const TRANSITION_DURATION_MS = 200;
const LIVE_REGION_DELAY_MS = 30;
const STICKY_GAP_PX = 24;
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]';

/**
 * Shop by Solution controller.
 *
 * The same product slide DOM is a Horizon carousel below 750px and a CSS grid
 * at larger breakpoints. This component owns solution state only. Product loop
 * behavior remains delegated to Horizon's public slideshow API.
 */
export class UlifShopBySolution extends Component {
  activeSolutionId = '';

  activeSolutionIndex = 0;

  /** @type {Map<string, number>} */
  productIndexBySolution = new Map();

  /** @type {Map<string, HTMLElement>} */
  hydratedPanels = new Map();

  carouselMode = false;

  verticalIndexMode = false;

  isTransitioning = false;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {MediaQueryList} */
  #gridMedia = window.matchMedia(GRID_QUERY);

  /** @type {MediaQueryList} */
  #verticalIndexMedia = window.matchMedia(VERTICAL_INDEX_QUERY);

  /** @type {Array<{ media: MediaQueryList, listener: (event: MediaQueryListEvent) => void, legacy: boolean }>} */
  #mediaListeners = [];

  /** @type {HTMLAnchorElement[]} */
  #tabs = [];

  /** @type {HTMLTemplateElement[]} */
  #templates = [];

  /** @type {number | undefined} */
  #transitionTimer;

  /** @type {number | undefined} */
  #liveRegionTimer;

  /** @type {number | undefined} */
  #restoreFrame;

  /** @type {number | undefined} */
  #updatedFrame;

  #selectionRevision = 0;

  #connected = false;

  /** @type {WeakMap<HTMLElement, string | null>} */
  #savedTabIndexes = new WeakMap();

  connectedCallback() {
    super.connectedCallback();

    this.#teardownController();
    this.#connected = true;
    this.#abortController = new AbortController();

    this.#refreshDomCollections();
    this.#bindEvents();

    this.carouselMode = !this.#gridMedia.matches;
    this.verticalIndexMode = this.#verticalIndexMedia.matches;

    const historyEntry = this.#readHistoryEntry(history.state);
    if (historyEntry) {
      this.productIndexBySolution.set(historyEntry.solutionId, historyEntry.productIndex);
    }

    const initialIndex = this.#resolveInitialSolutionIndex(historyEntry?.solutionId);
    if (initialIndex >= 0) {
      this.selectSolution(initialIndex, undefined, {
        announce: false,
        animate: false,
        focusTab: false,
        scrollStage: false,
        writeHistory: false,
      });
    } else {
      this.syncResponsiveMode();
    }
  }

  updatedCallback() {
    super.updatedCallback();

    if (!this.#connected || !this.#abortController || this.#updatedFrame !== undefined) return;

    this.#updatedFrame = window.requestAnimationFrame(() => {
      this.#updatedFrame = undefined;
      if (!this.#connected) return;

      const previousIndex = this.activeSolutionIndex;
      this.#refreshDomCollections();

      if (this.#tabs.length === 0) {
        this.activeSolutionId = '';
        this.activeSolutionIndex = 0;
        return;
      }

      let nextIndex = this.#findTabIndexBySolutionId(this.activeSolutionId);
      if (nextIndex < 0) nextIndex = Math.min(previousIndex, this.#tabs.length - 1);

      this.selectSolution(nextIndex, undefined, {
        announce: false,
        animate: false,
        focusTab: false,
        scrollStage: false,
        writeHistory: false,
      });
    });
  }

  disconnectedCallback() {
    this.#connected = false;
    this.#teardownController();
    super.disconnectedCallback();
  }

  /**
   * The only entry point for changing the active solution.
   *
   * @param {number|string} index
   * @param {Event} [event]
   * @param {Object} [options]
   * @param {boolean} [options.announce]
   * @param {boolean} [options.animate]
   * @param {boolean} [options.focusTab]
   * @param {boolean} [options.scrollStage]
   * @param {boolean} [options.writeHistory]
   * @returns {boolean}
   */
  selectSolution(index, event, options = {}) {
    this.#refreshDomCollections();

    const targetIndex = this.#normalizeSolutionIndex(index);
    const targetTab = this.#tabs[targetIndex];
    if (!targetTab) return false;

    const solutionId = this.#getSolutionId(targetTab);
    if (!solutionId) return false;

    const targetPanel = this.hydratePanel(solutionId);
    if (!targetPanel) return false;

    event?.preventDefault();

    const userInitiated = event != null;
    const announce = options.announce ?? userInitiated;
    const animate = options.animate ?? !prefersReducedMotion();
    const focusTab = options.focusTab ?? false;
    const scrollStage = options.scrollStage ?? userInitiated;
    const writeHistory = options.writeHistory ?? userInitiated;
    const previousSolutionId = this.activeSolutionId;

    if (previousSolutionId && previousSolutionId !== solutionId && this.carouselMode) {
      this.#saveCurrentProductIndex(previousSolutionId);
    }

    const revision = ++this.#selectionRevision;
    this.#startTransition(animate);

    this.activeSolutionId = solutionId;
    this.activeSolutionIndex = targetIndex;
    this.dataset.activeSolutionId = solutionId;

    this.#refreshDomCollections();
    this.#syncPanelStates(targetPanel);
    this.#syncTabStates(targetIndex);
    this.#syncSolutionMetadata(targetTab, targetPanel);
    this.syncResponsiveMode({ restoreActive: false });
    this.restoreProductIndex(solutionId, revision);

    if (focusTab) targetTab.focus({ preventScroll: true });
    this.#revealActiveTab(targetTab, animate);

    if (scrollStage) this.#alignStageToStickyOffset(animate);
    if (announce) this.#announceSolution(targetTab, targetPanel);
    if (writeHistory) this.#writeHistoryState();

    return true;
  }

  /**
   * Clone an inactive solution panel from its inert template once.
   *
   * @param {string} solutionId
   * @returns {HTMLElement|null}
   */
  hydratePanel(solutionId) {
    const cachedPanel = this.hydratedPanels.get(solutionId);
    if (cachedPanel?.isConnected && this.contains(cachedPanel)) return cachedPanel;

    const existingPanel = this.#findPanel(solutionId);
    if (existingPanel) {
      this.hydratedPanels.set(solutionId, existingPanel);
      return existingPanel;
    }

    const template = this.#findTemplate(solutionId);
    if (!template?.content) return null;

    const fragment = template.content.cloneNode(true);
    if (!(fragment instanceof DocumentFragment) || fragment.childNodes.length === 0) return null;

    let panel = Array.from(fragment.querySelectorAll('[data-solution-panel]')).find(
      (candidate) => this.#getSolutionId(candidate) === solutionId
    );

    if (!(panel instanceof HTMLElement)) {
      const firstPanel = fragment.querySelector('[data-solution-panel]');
      if (firstPanel instanceof HTMLElement) {
        panel = firstPanel;
        panel.dataset.solutionId = solutionId;
      }
    }

    if (!(panel instanceof HTMLElement)) {
      panel = document.createElement('section');
      panel.dataset.solutionPanel = '';
      panel.dataset.solutionId = solutionId;
      panel.setAttribute('role', 'tabpanel');
      panel.append(fragment);
    }

    const tab = this.#tabs[this.#findTabIndexBySolutionId(solutionId)];
    if (tab) {
      const controlledId = tab.getAttribute('aria-controls');
      if (controlledId && !panel.id) panel.id = controlledId;
      if (tab.id && !panel.getAttribute('aria-labelledby')) panel.setAttribute('aria-labelledby', tab.id);
    }

    this.#setPanelActive(panel, false);

    if (template.isConnected) {
      template.before(panel);
    } else {
      const stage = this.querySelector('[data-solution-stage]');
      stage?.append(panel);
    }

    template.dataset.hydrated = 'true';
    this.hydratedPanels.set(solutionId, panel);
    this.#refreshDomCollections();
    return panel;
  }

  /**
   * Restore the saved mobile product index for one solution.
   *
   * @param {string} solutionId
   * @param {number} [revision]
   */
  restoreProductIndex(solutionId, revision = this.#selectionRevision) {
    const panel = this.hydratedPanels.get(solutionId) ?? this.#findPanel(solutionId);
    if (!panel) return;

    const slideshow = this.#getPanelSlideshow(panel);
    if (!slideshow) return;

    if (!this.carouselMode) {
      this.syncSlideAccessibility(slideshow);
      return;
    }

    if (this.#restoreFrame !== undefined) window.cancelAnimationFrame(this.#restoreFrame);

    this.#restoreFrame = window.requestAnimationFrame(() => {
      this.#restoreFrame = undefined;
      if (!this.#connected || revision !== this.#selectionRevision || this.activeSolutionId !== solutionId) return;

      const restore = () => {
        if (!this.#connected || revision !== this.#selectionRevision || this.activeSolutionId !== solutionId) return;

        const slides = this.#getOwnedSlides(slideshow);
        if (slides.length === 0) return;

        const savedIndex = this.#clampProductIndex(this.productIndexBySolution.get(solutionId) ?? 0, slides.length);
        this.productIndexBySolution.set(solutionId, savedIndex);

        if (typeof slideshow.select !== 'function') return;

        const selection = slideshow.select(savedIndex, undefined, { animate: false });
        this.syncSlideAccessibility(slideshow);

        Promise.resolve(selection).then(() => {
          if (this.#connected && this.activeSolutionId === solutionId) {
            this.syncSlideAccessibility(slideshow);
          }
        });
      };

      if (typeof slideshow.select === 'function') {
        restore();
      } else {
        customElements.whenDefined('slideshow-component').then(restore);
      }
    });
  }

  /**
   * Synchronize carousel and grid behavior for all hydrated panels.
   *
   * @param {Object} [options]
   * @param {boolean} [options.restoreActive]
   */
  syncResponsiveMode(options = {}) {
    const restoreActive = options.restoreActive ?? true;
    const nextCarouselMode = !this.#gridMedia.matches;

    if (this.carouselMode && !nextCarouselMode && this.activeSolutionId) {
      this.#saveCurrentProductIndex(this.activeSolutionId);
    }

    this.carouselMode = nextCarouselMode;
    this.verticalIndexMode = this.#verticalIndexMedia.matches;
    this.dataset.solutionMode = this.carouselMode ? 'carousel' : 'grid';
    this.dataset.indexOrientation = this.verticalIndexMode ? 'vertical' : 'horizontal';

    for (const tablist of this.querySelectorAll('[data-solution-tabs], [role="tablist"]')) {
      tablist.setAttribute('role', 'tablist');
      tablist.setAttribute('aria-orientation', this.verticalIndexMode ? 'vertical' : 'horizontal');
    }

    for (const panel of this.hydratedPanels.values()) {
      if (!panel.isConnected) continue;

      const slideshow = this.#getPanelSlideshow(panel);
      if (!slideshow) continue;

      slideshow.setAttribute('disabled', this.carouselMode ? 'false' : 'true');
      slideshow.dataset.solutionMode = this.carouselMode ? 'carousel' : 'grid';
      this.#syncSlideshowControls(slideshow, this.carouselMode && this.#getSolutionId(panel) === this.activeSolutionId);

      if (!this.carouselMode) {
        this.syncSlideAccessibility(slideshow);
      } else if (this.#getSolutionId(panel) !== this.activeSolutionId) {
        for (const slide of this.#getOwnedSlides(slideshow)) {
          slide.setAttribute('aria-hidden', 'true');
          this.#setSlideInert(slide, true);
        }
      }
    }

    if (this.carouselMode && restoreActive && this.activeSolutionId) {
      this.restoreProductIndex(this.activeSolutionId);
    }
  }

  /**
   * Keep product links out of the tab order when their mobile slide is hidden.
   *
   * @param {HTMLElement} slideshow
   */
  syncSlideAccessibility(slideshow) {
    const slides = this.#getOwnedSlides(slideshow);
    if (slides.length === 0) return;

    const panel = slideshow.closest('[data-solution-panel]');
    const solutionId = panel ? this.#getSolutionId(panel) : '';

    if (!this.carouselMode) {
      for (const slide of slides) {
        slide.setAttribute('aria-hidden', 'false');
        this.#setSlideInert(slide, false);
      }
      return;
    }

    if (!solutionId || solutionId !== this.activeSolutionId) {
      for (const slide of slides) {
        slide.setAttribute('aria-hidden', 'true');
        this.#setSlideInert(slide, true);
      }
      return;
    }

    const selectedIndex = this.#clampProductIndex(this.productIndexBySolution.get(solutionId) ?? 0, slides.length);

    slides.forEach((slide, domIndex) => {
      const productIndex = Number.parseInt(slide.dataset.productIndex ?? '', 10);
      const logicalIndex = Number.isInteger(productIndex) ? productIndex : domIndex;
      const selected = logicalIndex === selectedIndex;
      slide.setAttribute('aria-hidden', String(!selected));
      this.#setSlideInert(slide, !selected);
    });

    for (const current of this.#getOwnedElements(slideshow, '[ref="current"]')) {
      current.textContent = String(selectedIndex + 1);
    }
  }

  /**
   * Manual activation keyboard model for the solution tabs.
   *
   * @param {KeyboardEvent} event
   */
  handleTabKeydown(event) {
    const target = event.target instanceof Element ? event.target.closest('[data-solution-tab]') : null;
    if (!(target instanceof HTMLAnchorElement) || !this.contains(target)) return;

    this.#refreshDomCollections();
    const currentIndex = this.#tabs.indexOf(target);
    if (currentIndex < 0) return;

    const horizontal = !this.verticalIndexMode;
    const rtl = getComputedStyle(this).direction === 'rtl';
    let focusIndex = -1;

    switch (event.key) {
      case 'ArrowUp':
        if (horizontal) return;
        focusIndex = currentIndex - 1;
        break;
      case 'ArrowDown':
        if (horizontal) return;
        focusIndex = currentIndex + 1;
        break;
      case 'ArrowLeft':
        if (!horizontal) return;
        focusIndex = currentIndex + (rtl ? 1 : -1);
        break;
      case 'ArrowRight':
        if (!horizontal) return;
        focusIndex = currentIndex + (rtl ? -1 : 1);
        break;
      case 'Home':
        focusIndex = 0;
        break;
      case 'End':
        focusIndex = this.#tabs.length - 1;
        break;
      case 'Enter':
      case ' ':
        this.selectSolution(currentIndex, event, {
          focusTab: true,
          scrollStage: true,
          writeHistory: true,
        });
        return;
      default:
        return;
    }

    if (focusIndex < 0 && this.#tabs.length > 0) focusIndex = this.#tabs.length - 1;
    if (focusIndex >= this.#tabs.length) focusIndex = 0;
    if (focusIndex < 0) return;

    event.preventDefault();
    this.#setRovingFocus(focusIndex);
    const focusTab = this.#tabs[focusIndex];
    focusTab?.focus({ preventScroll: true });
    if (focusTab && horizontal) this.#revealActiveTab(focusTab, !prefersReducedMotion());
  }

  #bindEvents() {
    const signal = this.#abortController?.signal;
    if (!signal) return;

    this.addEventListener('click', this.#handleClick, { signal });
    this.addEventListener('keydown', this.#handleKeydown, { signal });
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect, { signal });
    document.addEventListener('shopify:block:select', this.#handleThemeEditorBlockSelect, { signal });
    window.addEventListener('popstate', this.#handlePopState, { signal });
    window.addEventListener('pageshow', this.#handlePageShow, { signal });

    this.#bindMediaListener(this.#gridMedia, this.#handleResponsiveChange);
    this.#bindMediaListener(this.#verticalIndexMedia, this.#handleResponsiveChange);
  }

  /** @param {MediaQueryList} media */
  #bindMediaListener(media, listener) {
    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', listener);
      this.#mediaListeners.push({ media, listener, legacy: false });
      return;
    }

    media.addListener(listener);
    this.#mediaListeners.push({ media, listener, legacy: true });
  }

  #handleClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const tab = target.closest('[data-solution-tab]');
    if (tab instanceof HTMLAnchorElement && this.contains(tab)) {
      if (
        event instanceof MouseEvent &&
        (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
      ) {
        return;
      }

      const index = this.#tabs.indexOf(tab);
      if (index >= 0) {
        this.selectSolution(index, event, {
          focusTab: false,
          scrollStage: true,
          writeHistory: true,
        });
      }
      return;
    }

    const link = target.closest('[data-solution-product-link], [data-solution-panel] a[href]');
    const activePanel = link?.closest('[data-solution-panel]');
    if (link && activePanel && this.#getSolutionId(activePanel) === this.activeSolutionId) {
      this.#saveCurrentProductIndex(this.activeSolutionId);
      this.#writeHistoryState();
    }
  };

  #handleKeydown = (event) => {
    if (event instanceof KeyboardEvent) this.handleTabKeydown(event);
  };

  #handleSlideshowSelect = (event) => {
    const eventTarget = event.target;
    if (!(eventTarget instanceof HTMLElement) || eventTarget.tagName.toLowerCase() !== 'slideshow-component') return;

    const panel = eventTarget.closest('[data-solution-panel]');
    if (!panel || !this.contains(panel) || this.#getPanelSlideshow(panel) !== eventTarget) return;

    const solutionId = this.#getSolutionId(panel);
    const slides = this.#getOwnedSlides(eventTarget);
    if (!solutionId || slides.length === 0) return;

    const detail = /** @type {{ index?: number, userInitiated?: boolean }} */ (event.detail ?? {});
    const index = this.#clampProductIndex(Number(detail.index) || 0, slides.length);
    this.productIndexBySolution.set(solutionId, index);
    this.syncSlideAccessibility(eventTarget);

    if (detail.userInitiated && solutionId === this.activeSolutionId) this.#writeHistoryState();
  };

  #handleResponsiveChange = () => {
    if (!this.#connected) return;
    this.syncResponsiveMode();
  };

  #handleThemeEditorBlockSelect = (event) => {
    const detail = /** @type {{ blockId?: string }} */ (event.detail ?? {});
    const eventTarget = event.target instanceof Element ? event.target : null;
    const blockId = detail.blockId ?? eventTarget?.closest('[data-block-id]')?.getAttribute('data-block-id');
    if (!blockId) return;

    this.#refreshDomCollections();
    const index = this.#tabs.findIndex(
      (tab) => tab.dataset.blockId === blockId || this.#getSolutionId(tab) === blockId
    );
    if (index < 0) return;

    this.selectSolution(index, undefined, {
      announce: false,
      animate: false,
      focusTab: false,
      scrollStage: false,
      writeHistory: false,
    });
  };

  #handlePopState = (event) => {
    this.#restoreFromHistory(event.state);
  };

  #handlePageShow = () => {
    this.#restoreFromHistory(history.state);
  };

  #restoreFromHistory(state) {
    const entry = this.#readHistoryEntry(state);
    if (!entry) return;

    const index = this.#findTabIndexBySolutionId(entry.solutionId);
    if (index < 0) return;

    this.productIndexBySolution.set(entry.solutionId, entry.productIndex);
    this.selectSolution(index, undefined, {
      announce: false,
      animate: false,
      focusTab: false,
      scrollStage: false,
      writeHistory: false,
    });
  }

  #refreshDomCollections() {
    this.#tabs = Array.from(this.querySelectorAll('[data-solution-tab]')).filter(
      (tab) => tab instanceof HTMLAnchorElement
    );
    this.#templates = Array.from(this.querySelectorAll('template[data-solution-template]'));

    for (const [solutionId, panel] of this.hydratedPanels) {
      if (!panel.isConnected || !this.contains(panel)) this.hydratedPanels.delete(solutionId);
    }

    for (const panel of this.querySelectorAll('[data-solution-panel]')) {
      if (!(panel instanceof HTMLElement)) continue;
      const solutionId = this.#getSolutionId(panel);
      if (solutionId) this.hydratedPanels.set(solutionId, panel);
    }
  }

  #resolveInitialSolutionIndex(historySolutionId) {
    if (historySolutionId) {
      const historyIndex = this.#findTabIndexBySolutionId(historySolutionId);
      if (historyIndex >= 0) return historyIndex;
    }

    const configuredId = this.dataset.activeSolutionId;
    if (configuredId) {
      const configuredIndex = this.#findTabIndexBySolutionId(configuredId);
      if (configuredIndex >= 0) return configuredIndex;
    }

    const selectedIndex = this.#tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    return selectedIndex >= 0 ? selectedIndex : this.#tabs.length > 0 ? 0 : -1;
  }

  #normalizeSolutionIndex(index) {
    if (typeof index === 'string' && !/^\d+$/.test(index)) return this.#findTabIndexBySolutionId(index);

    const numericIndex = Number(index);
    if (!Number.isInteger(numericIndex) || numericIndex < 0 || numericIndex >= this.#tabs.length) return -1;
    return numericIndex;
  }

  #findTabIndexBySolutionId(solutionId) {
    if (!solutionId) return -1;
    return this.#tabs.findIndex((tab) => this.#getSolutionId(tab) === solutionId);
  }

  #findPanel(solutionId) {
    return (
      Array.from(this.querySelectorAll('[data-solution-panel]')).find(
        (panel) => panel instanceof HTMLElement && this.#getSolutionId(panel) === solutionId
      ) ?? null
    );
  }

  #findTemplate(solutionId) {
    return (
      this.#templates.find(
        (template) =>
          this.#getSolutionId(template) === solutionId || template.dataset.solutionTemplate === solutionId
      ) ?? null
    );
  }

  #getSolutionId(element) {
    return element?.dataset?.solutionId || element?.dataset?.solutionTemplate || '';
  }

  #getPanelSlideshow(panel) {
    const slideshow = panel.querySelector('slideshow-component');
    return slideshow instanceof HTMLElement ? slideshow : null;
  }

  #getOwnedSlides(slideshow) {
    return Array.from(slideshow.querySelectorAll('slideshow-slide')).filter(
      (slide) =>
        slide instanceof HTMLElement &&
        slide.closest('slideshow-component') === slideshow &&
        (slide.hasAttribute('data-solution-product-slide') || slide.dataset.productIndex !== undefined)
    );
  }

  #getOwnedElements(slideshow, selector) {
    return Array.from(slideshow.querySelectorAll(selector)).filter(
      (element) => element instanceof HTMLElement && element.closest('slideshow-component') === slideshow
    );
  }

  #syncPanelStates(activePanel) {
    for (const panel of this.hydratedPanels.values()) {
      if (!panel.isConnected) continue;
      this.#setPanelActive(panel, panel === activePanel);
    }
  }

  #setPanelActive(panel, active) {
    panel.setAttribute('role', 'tabpanel');
    panel.hidden = !active;
    panel.toggleAttribute('inert', !active);
    if ('inert' in panel) panel.inert = !active;
    panel.setAttribute('aria-hidden', String(!active));
    panel.toggleAttribute('data-active', active);
  }

  #syncTabStates(activeIndex) {
    this.#tabs.forEach((tab, index) => {
      const active = index === activeIndex;
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-selected', String(active));
      tab.setAttribute('tabindex', active ? '0' : '-1');
      tab.classList.toggle('is-active', active);
      tab.toggleAttribute('data-active', active);
    });
  }

  #setRovingFocus(focusIndex) {
    this.#tabs.forEach((tab, index) => tab.setAttribute('tabindex', index === focusIndex ? '0' : '-1'));
  }

  #syncSolutionMetadata(tab, panel) {
    const title = panel.dataset.solutionTitle || tab.dataset.solutionTitle || tab.textContent?.trim() || '';
    const descriptionSource = panel.querySelector('[data-solution-description]');
    const count = this.#getProductCount(tab, panel);
    const ctaSource = panel.querySelector('[data-solution-cta]');
    const description =
      descriptionSource?.getAttribute('data-solution-description-value') ??
      descriptionSource?.textContent?.trim() ??
      panel.dataset.description ??
      tab.dataset.solutionDescription ??
      tab.dataset.description ??
      '';
    const ctaLabel =
      ctaSource?.getAttribute('data-solution-cta-label') ??
      ctaSource?.textContent?.trim() ??
      panel.dataset.ctaLabel ??
      tab.dataset.solutionCtaLabel ??
      tab.dataset.ctaLabel ??
      '';
    const ctaUrl =
      ctaSource?.getAttribute('href') ??
      ctaSource?.getAttribute('data-solution-cta-url') ??
      panel.dataset.ctaUrl ??
      tab.dataset.solutionCtaUrl ??
      tab.dataset.ctaUrl ??
      '';

    for (const target of this.querySelectorAll('[data-solution-title-output]')) {
      if (panel.contains(target)) continue;
      target.textContent = title;
    }

    for (const target of this.querySelectorAll('[data-solution-description]')) {
      if (panel.contains(target)) continue;
      target.textContent = description;
      target.toggleAttribute('hidden', description.length === 0);
    }

    for (const target of this.querySelectorAll('[data-solution-count]')) {
      if (panel.contains(target)) continue;
      const suffix = target.dataset.solutionCountSuffix ?? (count === 1 ? 'product' : 'products');
      target.textContent = `${count} ${suffix}`;
      target.dataset.productCount = String(count);
    }

    for (const target of this.querySelectorAll('[data-solution-cta]')) {
      if (panel.contains(target) || !(target instanceof HTMLAnchorElement)) continue;

      if (ctaUrl) {
        target.href = ctaUrl;
        target.removeAttribute('aria-disabled');
        target.hidden = false;
      } else {
        target.removeAttribute('href');
        target.setAttribute('aria-disabled', 'true');
        target.hidden = true;
      }

      target.textContent = ctaLabel || title;
      target.setAttribute('aria-label', ctaLabel || title);
    }
  }

  #getProductCount(tab, panel) {
    const slideshow = this.#getPanelSlideshow(panel);
    const countSource = panel.querySelector('[data-solution-count]');
    const candidates = [
      panel.dataset.productCount,
      slideshow?.dataset.productCount,
      countSource?.getAttribute('data-product-count'),
      tab.dataset.productCount,
      slideshow ? this.#getOwnedSlides(slideshow).length : 0,
    ];

    for (const candidate of candidates) {
      if (candidate === undefined || candidate === null || candidate === '') continue;
      const count = Number(candidate);
      if (Number.isInteger(count) && count >= 0) return count;
    }

    return 0;
  }

  #announceSolution(tab, panel) {
    const title = tab.dataset.solutionTitle || tab.textContent?.trim() || '';
    const count = this.#getProductCount(tab, panel);
    const message = `${title}, ${count} ${count === 1 ? 'product' : 'products'}`;
    const regions = Array.from(this.querySelectorAll('[data-solution-live]'));
    if (regions.length === 0) return;

    if (this.#liveRegionTimer !== undefined) window.clearTimeout(this.#liveRegionTimer);
    for (const region of regions) region.textContent = '';

    this.#liveRegionTimer = window.setTimeout(() => {
      this.#liveRegionTimer = undefined;
      if (!this.#connected) return;
      for (const region of regions) region.textContent = message;
    }, LIVE_REGION_DELAY_MS);
  }

  #saveCurrentProductIndex(solutionId) {
    const panel = this.hydratedPanels.get(solutionId) ?? this.#findPanel(solutionId);
    const slideshow = panel ? this.#getPanelSlideshow(panel) : null;
    if (!slideshow) return;

    const slides = this.#getOwnedSlides(slideshow);
    if (slides.length === 0) return;

    const current = Number(slideshow.current);
    if (Number.isInteger(current)) {
      this.productIndexBySolution.set(solutionId, this.#clampProductIndex(current, slides.length));
    }
  }

  #clampProductIndex(index, count) {
    if (count <= 0) return 0;
    const numericIndex = Number.isFinite(Number(index)) ? Math.trunc(Number(index)) : 0;
    return Math.min(Math.max(numericIndex, 0), count - 1);
  }

  #syncSlideshowControls(slideshow, enabled) {
    for (const controls of this.#getOwnedElements(
      slideshow,
      'slideshow-controls, [data-solution-controls], .ulif-shop-by-solution__mobile-controls'
    )) {
      controls.setAttribute('aria-hidden', String(!enabled));
      controls.toggleAttribute('inert', !enabled);
      if ('inert' in controls) controls.inert = !enabled;
    }
  }

  #setSlideInert(slide, inert) {
    slide.toggleAttribute('inert', inert);
    if ('inert' in slide) slide.inert = inert;

    for (const element of slide.querySelectorAll(FOCUSABLE_SELECTOR)) {
      if (!(element instanceof HTMLElement)) continue;

      if (inert) {
        if (!this.#savedTabIndexes.has(element)) {
          this.#savedTabIndexes.set(element, element.getAttribute('tabindex'));
        }
        element.setAttribute('tabindex', '-1');
      } else if (this.#savedTabIndexes.has(element)) {
        const previousTabIndex = this.#savedTabIndexes.get(element);
        if (previousTabIndex === null) element.removeAttribute('tabindex');
        else if (previousTabIndex !== undefined) element.setAttribute('tabindex', previousTabIndex);
        this.#savedTabIndexes.delete(element);
      }
    }
  }

  #revealActiveTab(tab, animate) {
    if (this.verticalIndexMode || typeof tab.scrollIntoView !== 'function') return;

    tab.scrollIntoView({
      behavior: animate && !prefersReducedMotion() ? 'smooth' : 'auto',
      block: 'nearest',
      inline: 'nearest',
    });
  }

  #alignStageToStickyOffset(animate) {
    const stage = this.querySelector('[data-solution-stage]');
    if (!(stage instanceof HTMLElement)) return;

    const offset = this.#getStickyOffset();
    const top = stage.getBoundingClientRect().top;
    if (top >= offset) return;

    window.scrollTo({
      top: Math.max(0, window.scrollY + top - offset),
      behavior: animate && !prefersReducedMotion() ? 'smooth' : 'auto',
    });
  }

  #getStickyOffset() {
    const styles = getComputedStyle(document.body);
    const headerHeight = Number.parseFloat(styles.getPropertyValue('--header-height'));

    // Mobile and tablet tabs use the sticky header/search row, while the desktop rail keeps its wider group offset.
    if (!this.#verticalIndexMedia.matches) {
      // Horizon positions its sticky header at -1px so its observer can detect the sticky state.
      return Number.isFinite(headerHeight) ? Math.max(0, headerHeight - 1) : 0;
    }

    const groupHeight = Number.parseFloat(styles.getPropertyValue('--header-group-height'));
    const headerOffset = Number.isFinite(groupHeight) && groupHeight > 0 ? groupHeight : headerHeight;
    return (Number.isFinite(headerOffset) ? headerOffset : 0) + STICKY_GAP_PX;
  }

  #startTransition(animate) {
    if (this.#transitionTimer !== undefined) window.clearTimeout(this.#transitionTimer);

    this.isTransitioning = animate && !prefersReducedMotion();
    this.toggleAttribute('data-transitioning', this.isTransitioning);

    if (!this.isTransitioning) return;

    this.#transitionTimer = window.setTimeout(() => {
      this.#transitionTimer = undefined;
      this.isTransitioning = false;
      this.removeAttribute('data-transitioning');
    }, TRANSITION_DURATION_MS);
  }

  #getSectionId() {
    return this.dataset.sectionId || this.id || 'default';
  }

  #readHistoryEntry(state) {
    if (!state || typeof state !== 'object') return null;

    const namespace = state[HISTORY_NAMESPACE];
    if (!namespace || typeof namespace !== 'object') return null;

    const entry = namespace[this.#getSectionId()];
    if (!entry || typeof entry !== 'object' || typeof entry.solutionId !== 'string') return null;

    return {
      solutionId: entry.solutionId,
      productIndex: this.#clampProductIndex(entry.productIndex ?? 0, Number.MAX_SAFE_INTEGER),
    };
  }

  #writeHistoryState() {
    if (!this.activeSolutionId) return;

    const previousState = history.state && typeof history.state === 'object' ? history.state : {};
    const previousNamespace =
      previousState[HISTORY_NAMESPACE] && typeof previousState[HISTORY_NAMESPACE] === 'object'
        ? previousState[HISTORY_NAMESPACE]
        : {};
    const nextState = {
      ...previousState,
      [HISTORY_NAMESPACE]: {
        ...previousNamespace,
        [this.#getSectionId()]: {
          solutionId: this.activeSolutionId,
          productIndex: this.productIndexBySolution.get(this.activeSolutionId) ?? 0,
        },
      },
    };

    try {
      history.replaceState(nextState, document.title);
    } catch (error) {
      if (globalThis.Shopify?.designMode) console.warn('Unable to save Shop by Solution history state.', error);
    }
  }

  #teardownController() {
    this.#abortController?.abort();
    this.#abortController = undefined;

    for (const { media, listener, legacy } of this.#mediaListeners) {
      if (legacy) media.removeListener(listener);
      else media.removeEventListener('change', listener);
    }
    this.#mediaListeners = [];

    if (this.#transitionTimer !== undefined) window.clearTimeout(this.#transitionTimer);
    if (this.#liveRegionTimer !== undefined) window.clearTimeout(this.#liveRegionTimer);
    if (this.#restoreFrame !== undefined) window.cancelAnimationFrame(this.#restoreFrame);
    if (this.#updatedFrame !== undefined) window.cancelAnimationFrame(this.#updatedFrame);

    this.#transitionTimer = undefined;
    this.#liveRegionTimer = undefined;
    this.#restoreFrame = undefined;
    this.#updatedFrame = undefined;
    this.isTransitioning = false;
    this.removeAttribute('data-transitioning');
  }
}

if (!customElements.get('ulif-shop-by-solution')) {
  customElements.define('ulif-shop-by-solution', UlifShopBySolution);
}
